// ══════════════════════════════════════════════════════════════════════════
//  [WPP1 2026-08-05] PARTS / OEM IDENTIFICATION ENGINE — the v1.2 parts-uplift lane.
//
//  The contract is docs/classifier-oem-part-identification-playbook.md. Its output
//  shape, decision hierarchy, normalization rules, fail-loud conditions and 10
//  acceptance tests are binding; test/wp-p1-oem-parts.test.js is that spine.
//
//  Law inherited from the playbook, restated because every edit below must respect it:
//    • part-number SHAPE is evidence, never proof;
//    • UNKNOWN never silently becomes OEM;
//    • nothing here silently deletes an operation — it returns facts and a routing code;
//    • structured part numbers are NEVER redacted (some valid ones look like phone numbers).
//
//  Blake's 2026-08-05 amendment splits the playbook's CHEMICAL_SUPPLY in two:
//    FLUID           — a substance installed INTO the vehicle as part of the repair
//                      (engine oil, coolant, brake/trans fluid, refrigerant): CLAIMABLE.
//    CHEMICAL_SUPPLY — cleaners, treatments, additives-as-service, dyes, adhesives,
//                      greases, shop supplies: NOT claimable.
//  Both behave identically for line ROUTING (the incidental-share logic); only
//  claimability differs.
//
//  DEPENDENCY-FREE by design, like every other src/pipeline/ module: no React, no DOM,
//  no fetch, no storage. The approved-mapping ledger and the AI brand/OEM verdicts are
//  INJECTED by the caller (the advisory-runner dependency pattern), so this file runs
//  identically in the browser, in api/selftest.js, and under `node --test`.
//
//  This module is NOT wired into the repair pipeline. Parts eligibility is a separate
//  per-line dimension consumed by WP-P2; classifyDet and the post-passes are untouched.
// ══════════════════════════════════════════════════════════════════════════

export const OEM_PART_RULE_VERSION = "oem-part-v1";
export const OEM_MAPPINGS_KEY = "parts-oem-mappings-v1";

// The classification vocabulary. Closed set — a value outside it is a bug, and the
// tests pin the list so a silent addition can't reach production.
export const PART_CLASSES = [
  "OEM_CONFIRMED", "OEM_PROBABLE", "NON_OEM_CONFIRMED", "NON_OEM_PROBABLE",
  "FLUID", "CHEMICAL_SUPPLY", "UNKNOWN",
];

// Line-level routing vocabulary. Also closed.
export const PARTS_ROUTES = [
  "OEM_CLEAN", "INCIDENTAL_CHEMICAL", "MIXED_PARTS_REVIEW", "NON_OEM_MAIN_PART",
  "NON_OEM_DOMINANT", "UNKNOWN_PART_REVIEW", "CHEMICAL_SERVICE_REVIEW", "PARTS_FAIL_LOUD",
  "NO_PARTS_DATA",
];

// Every evidence code the engine can emit. Closed vocabulary — an auditor reads these.
export const PART_EVIDENCE_CODES = [
  "MAPPING_DEALER", "MAPPING_GROUP", "MAPPING_DMS", "MAPPING_GLOBAL", "MAPPING_CONFLICT",
  "SOURCE_OEM_FLAG", "SOURCE_AFTERMARKET_FLAG", "SOURCE_FLAG_UNTRUSTED", "SOURCE_FLAG_CONFLICT",
  "BRAND_AFTERMARKET", "BRAND_SUPPLIER", "FORMAT_AFTERMARKET", "FORMAT_AFTERMARKET_BRANDED", "BRAND_OEM",
  "FORMAT_OEM_MAKE", "FORMAT_OEM_OTHER_MAKE", "MAKE_UNKNOWN",
  "DESC_OEM_WORD", "DESC_AFTERMARKET_WORD",
  "VOCAB_FLUID", "VOCAB_CHEMICAL",
  "AI_OEM", "AI_AFTERMARKET", "AI_FLUID", "AI_CHEMICAL", "AI_UNCERTAIN",
  "NO_PART_NUMBER", "NO_EVIDENCE", "NORMALIZED_COLLISION",
];

// Fail-loud codes — playbook §Fail-loud conditions.
export const PARTS_FAIL_CODES = [
  "MAPPING_CONFLICT", "FLAG_VS_BRAND_CONFLICT", "MATERIAL_PART_UNIDENTIFIABLE",
  "NORMALIZED_COLLISION", "PARTS_TOTAL_MISMATCH", "NO_STABLE_OPERATION", "RULE_VERSION_MISSING",
];

// ══════════════════════════════════════════════════════════════════════════
//  CONFIGURATION — program/version config, NOT hard-coded law (playbook: "Store it
//  as program/version configuration rather than hard-coding it"). Callers may pass
//  `ctx.config` to override any field; PARTS_CONFIG is the approved default set.
// ══════════════════════════════════════════════════════════════════════════
export const PARTS_CONFIG = {
  version: "parts-routing-v1",
  // Confirmed/probable non-OEM at or above this share of line parts sale = non-OEM dominant.
  nonOemDominantShare: 0.90,
  // Chemicals/fluids at or below this share of line parts sale are incidental to the repair.
  incidentalChemicalShare: 0.15,
  // An UNKNOWN part at or above this share of line parts sale is economically material.
  materialUnknownShare: 0.05,
  // Parts-total reconciliation tolerance (playbook fail-loud: "part sales do not reconcile
  // to the operation's parts total"). Whichever is larger wins, so cent-rounding and
  // ordinary discount noise don't flood review.
  reconcileToleranceAbs: 1.00,
  reconcileToleranceShare: 0.01,
  // A DMS OEM flag is only authoritative when the caller says that DMS's flag is trustworthy
  // (playbook §2: "Do not treat a generic stock group or a blank manufacturer field as proof").
  // Default OFF: an untrusted flag degrades to OEM_PROBABLE / NON_OEM_PROBABLE.
  trustSourceFlags: false,
};

// Confidence by evidence tier. Numbers are comparable across parts, nothing more.
const CONF = {
  mapping: 0.99,
  trustedFlag: 0.92,
  brandAndFormat: 0.90,
  vocab: 0.85,
  brandOnly: 0.70,
  formatOnly: 0.60,
  makeFormat: 0.65,
  ai: 0.55,
  none: 0,
};

// ══════════════════════════════════════════════════════════════════════════
//  NORMALIZATION — playbook §Normalization.
//  Raw is stored unchanged, forever. Normalized is trim + uppercase + unicode
//  punctuation folded. `strippedKey` (separators removed) is a SECOND comparison key
//  and is only produced for makes/catalogs where separators are known to be
//  insignificant — hyphens are meaningful in Dorman-style numbering, so stripping
//  globally would collide distinct parts. Leading zeroes are NEVER discarded.
//
//  NOTHING in this file redacts a part number. Some valid part numbers look like
//  telephone numbers; PII cleaning belongs to free text, not structured identifiers.
// ══════════════════════════════════════════════════════════════════════════

// Unicode punctuation that DMS exports smuggle in: en/em/figure dashes, minus sign,
// non-breaking hyphen and space, curly quotes, ideographic space.
const UNICODE_FOLD = [
  [/[‐‑‒–—―−]/g, "-"],
  [/[‘’ʼ]/g, "'"],
  [/[“”]/g, '"'],
  [/[   　]/g, " "],
];

// Catalog FAMILIES whose numbering treats separators as decoration, so a separator-free
// second key is safe. Everything else keeps its separators (Dorman's 599-981 is not 599981).
export const SEPARATOR_INSENSITIVE_FAMILIES = new Set(["MOPAR"]);

export function normalizePartNumber(raw, opts = {}) {
  const original = raw == null ? "" : String(raw);
  let normalized = original;
  for (const [re, to] of UNICODE_FOLD) normalized = normalized.replace(re, to);
  normalized = normalized.trim().replace(/\s+/g, " ").toUpperCase();
  const family = makeFamily(opts.make);
  const separatorInsensitive = !!family && SEPARATOR_INSENSITIVE_FAMILIES.has(family);
  const strippedKey = separatorInsensitive ? normalized.replace(/[-.\s/]/g, "") : null;
  return { raw: original, normalized, strippedKey, separatorInsensitive };
}

// Make names as they arrive from a DMS/VIN column, folded to one spelling.
const MAKE_ALIASES = {
  CHRYSLER: "CHRYSLER", DODGE: "DODGE", JEEP: "JEEP", RAM: "RAM", FIAT: "FIAT", MOPAR: "MOPAR",
  CDJR: "CHRYSLER", "CHRYSLER DODGE JEEP RAM": "CHRYSLER",
  BMW: "BMW", MINI: "MINI", "MERCEDES": "MERCEDES", "MERCEDES-BENZ": "MERCEDES", MB: "MERCEDES",
  TOYOTA: "TOYOTA", LEXUS: "LEXUS", SCION: "TOYOTA", HONDA: "HONDA", ACURA: "ACURA",
  NISSAN: "NISSAN", INFINITI: "INFINITI", MAZDA: "MAZDA", SUBARU: "SUBARU", MITSUBISHI: "MITSUBISHI",
  KIA: "KIA", HYUNDAI: "HYUNDAI", GENESIS: "GENESIS",
  FORD: "FORD", LINCOLN: "LINCOLN", MOTORCRAFT: "FORD",
  CHEVROLET: "CHEVROLET", CHEVY: "CHEVROLET", GMC: "GMC", BUICK: "BUICK", CADILLAC: "CADILLAC",
  ACDELCO: "CHEVROLET", VOLKSWAGEN: "VOLKSWAGEN", VW: "VOLKSWAGEN", AUDI: "AUDI", PORSCHE: "PORSCHE",
  VOLVO: "VOLVO", "LAND ROVER": "LAND ROVER", JAGUAR: "JAGUAR", TESLA: "TESLA",
};
export function canonicalMake(make) {
  const k = String(make || "").trim().toUpperCase();
  if (!k) return null;
  return MAKE_ALIASES[k] || k;
}

// Catalog families: one numbering system covers several marques. A Ram and a Jeep both
// wear Mopar numbers, so "the part's catalog make" is a FAMILY question, not a badge one.
export const MAKE_FAMILIES = {
  MOPAR: ["CHRYSLER", "DODGE", "JEEP", "RAM", "FIAT", "MOPAR"],
  BMW: ["BMW", "MINI"],
  TOYOTA: ["TOYOTA", "LEXUS"],
  HONDA: ["HONDA", "ACURA"],
  HYUNDAI: ["HYUNDAI", "KIA", "GENESIS"],
  FORD: ["FORD", "LINCOLN"],
  GM: ["CHEVROLET", "GMC", "BUICK", "CADILLAC"],
  VAG: ["VOLKSWAGEN", "AUDI", "PORSCHE"],
  NISSAN: ["NISSAN", "INFINITI"],
  MERCEDES: ["MERCEDES"],
};
const FAMILY_OF = (() => {
  const m = {};
  for (const [family, makes] of Object.entries(MAKE_FAMILIES)) for (const mk of makes) m[mk] = family;
  return m;
})();
export function makeFamily(make) {
  const c = canonicalMake(make);
  if (!c) return null;
  return FAMILY_OF[c] || c;
}

// ══════════════════════════════════════════════════════════════════════════
//  VOCABULARY + FORMAT TABLES — DATA, and CLASSIFICATION-AFFECTING for parts
//  projects. Seeded from the playbook's Gilroy evidence. Editing these changes what
//  the tool calls OEM, so they follow the same approval discipline as the law tables
//  in tables.js.
// ══════════════════════════════════════════════════════════════════════════

// ── Chemicals / supplies (NOT claimable) ─────────────────────────────────────
// Tested BEFORE the fluid table on purpose: "coolant system cleaner" names a fluid
// but IS a chemical. MOC = the Mopar-branded additive-as-service family from the
// playbook (MOC Gear Guard, MOC Ultra Shift) — sold as a service, never claimable.
//
// WASHER FLUID / SOLVENT lives HERE, not in the fluid table (Blake, 2026-08-05):
// a claimable fluid RIDES THE REPAIR — brake fluid with a caliper, coolant with a water
// pump. Washer fluid never rides a warranty-like repair, so it is a supply, not a
// claimable installed fluid.
export const CHEMICAL_SUPPLY_RE = /\b(?:cleaner|kleen|klean|degreaser|parts\s*solvent|solvent\s*cleaner|brake\s*clean\w*|throttle\s*body\s*clean\w*|injector\s*clean\w*|induction\s*(?:service|clean\w*)|carbon\s*clean\w*|decarbon\w*|treatment|additive|conditioner|restore\w*|protectant|stop\s*leak|sealer|sealant|rtv|adhesive|epoxy|thread\s*lock\w*|loctite|primer|glue|dye|uv\s*dye|grease|anti[-\s]?seize|dielectric|lubricant\s*spray|penetrating\s*oil|silicone\s*spray|shop\s*supp\w*|hazmat|rags?|gloves|sandpaper|abrasive|masking|body\s*filler|undercoat\w*|paint|wax|polish|deodoriz\w*|disinfect\w*|a\/?c\s*(?:bomb|odor)|moc\b|gear\s*guard|ultra\s*shift|fuel\s*(?:system\s*)?(?:clean\w*|treatment)|engine\s*flush|washer\s*(?:fluid|solvent)|windshield\s*washer)\b/i;

// ── Fluids (CLAIMABLE — Blake 2026-08-05) ────────────────────────────────────
// A substance installed INTO the vehicle as part of the repair, and one that rides a
// repair: brake fluid with a caliper, coolant with a water pump. See the washer-fluid
// carve-out above.
export const FLUID_RE = /\b(?:motor\s*oil|engine\s*oil|bulk\s*oil|oil\s*(?:qt|quart|gallon|bulk)|\d+w-?\d+|atf|transmission\s*fluid|trans\s*fluid|gear\s*(?:oil|lube)|differential\s*(?:oil|fluid)|axle\s*(?:oil|fluid)|transfer\s*case\s*fluid|brake\s*fluid|dot\s*[345]|clutch\s*fluid|power\s*steering\s*fluid|hydraulic\s*(?:oil|fluid)|coolant|antifreeze|refrigerant|r-?134a|r-?1234yf|pag\s*oil|compressor\s*oil|def\b|diesel\s*exhaust\s*fluid|urea|distilled\s*water|battery\s*(?:acid|electrolyte))\b/i;

// ── Aftermarket brands ───────────────────────────────────────────────────────
// TIER 1 — brands that a franchise dealer's parts counter never sells AS the OEM part.
// Two paths reach NON_OEM_CONFIRMED (Blake, 2026-08-05): the BRAND FIELD naming one of
// these, or the NUMBER positively matching one of these brands' documented numbering
// (`confirmsByNumber`) with a description that does not contradict it. In parts mode a
// confirmed non-OEM MAIN part is an auto-exclude, so keep this list to brands where that
// is not arguable.
//
// `confirmsByNumber` is only true where the pattern can actually PLACE the number with
// one brand. Blake's guardrail: "aftermarket parts can be confirmed by number as long as
// the number is clearly not OEM" — and "clearly" means a positive match to a known
// aftermarket catalog pattern. The absence of an OEM-looking shape, an unfamiliar number
// and a blank brand field can NEVER confirm; they stay PROBABLE/UNKNOWN. If two brands'
// patterns both match, the number is not placeable → PROBABLE (see attributeByNumber).
export const AFTERMARKET_BRANDS = [
  // Dorman's documented ###-### product numbering (599-981, 615-311, 611-162, 947-021 —
  // the playbook's own Gilroy evidence). Wider ###-####/###-##### forms are NOT placeable
  // to one catalog and fall through to the generic shape table below.
  { re: /\bdorman\b/i, brand: "Dorman", format: /^\d{3}-\d{3}$/, confirmsByNumber: true },
  { re: /\b(?:a-?1\s*)?cardone\b/i, brand: "Cardone", format: /^\d{2}-\d{4,5}$/, confirmsByNumber: false },
  { re: /\bbeck\s*\/?\s*arnley\b/i, brand: "Beck/Arnley", format: /^\d{3}-\d{4}$/, confirmsByNumber: false },
  { re: /\bduralast\b/i, brand: "Duralast", format: null, confirmsByNumber: false },
  { re: /\bautozone\b/i, brand: "AutoZone", format: null, confirmsByNumber: false },
  { re: /\bo'?reilly\b/i, brand: "O'Reilly", format: null, confirmsByNumber: false },
  { re: /\bcarquest\b/i, brand: "Carquest", format: null, confirmsByNumber: false },
  { re: /\bnapa\b/i, brand: "NAPA", format: null, confirmsByNumber: false },
  { re: /\bmoog\b/i, brand: "Moog", format: null, confirmsByNumber: false },
  { re: /\braybestos\b/i, brand: "Raybestos", format: null, confirmsByNumber: false },
  { re: /\bwagner\b/i, brand: "Wagner", format: null, confirmsByNumber: false },
  { re: /\bcentric\b/i, brand: "Centric", format: null, confirmsByNumber: false },
  { re: /\bspectra\s*premium\b/i, brand: "Spectra Premium", format: null, confirmsByNumber: false },
  { re: /\bairtex\b/i, brand: "Airtex", format: null, confirmsByNumber: false },
];

// TIER 2 — genuine OE SUPPLIERS whose boxes also sell aftermarket. Seeing one of these
// names is real evidence, but never proof of aftermarket: the same company builds the
// part in the OEM box. Caps at NON_OEM_PROBABLE → Human Review, never an auto-exclude.
export const OE_SUPPLIER_BRANDS = [
  { re: /\bdenso\b/i, brand: "Denso" }, { re: /\baisin\b/i, brand: "Aisin" },
  { re: /\bbosch\b/i, brand: "Bosch" }, { re: /\bgates\b/i, brand: "Gates" },
  { re: /\bcontinental\b/i, brand: "Continental" }, { re: /\bvaleo\b/i, brand: "Valeo" },
  { re: /\bhella\b/i, brand: "Hella" }, { re: /\bmahle\b/i, brand: "Mahle" },
  { re: /\bskf\b/i, brand: "SKF" }, { re: /\btimken\b/i, brand: "Timken" },
  { re: /\bexide\b/i, brand: "Exide" }, { re: /\bmagneti\s*marelli\b/i, brand: "Magneti Marelli" },
];

// Aftermarket NUMBER formats that no single catalog in our table can claim. Shape only,
// so PROBABLE at most — the playbook's AC328 / TPS333 sensor-style identifiers live here
// precisely because we cannot place them with a brand.
export const AFTERMARKET_FORMATS = [
  { re: /^\d{3}-\d{4,5}$/, label: "aftermarket-style ###-####" },
  { re: /^(?:AC|TPS|ALS|SU|PC|CS|VS|ES|MT|TCS)\d{3,4}$/, label: "aftermarket sensor-style prefix+digits" },
];

// A description is "consistent" with an aftermarket attribution when it actually names a
// part and does not assert the opposite. A blank description or one that says GENUINE/OEM
// leaves the number unconfirmed — evidence must agree with itself.
const descriptionSupportsAftermarket = (description, haystack) =>
  String(description || "").trim().length >= 3 && !DESC_OEM_RE.test(haystack);

// OEM number formats by catalog FAMILY. A match is `OEM_PROBABLE` at best — a format is
// a shape, and the playbook is explicit that failing to match is NEVER evidence of
// aftermarket ("Mopar also legitimately uses other formats… therefore, failure to match
// the common pattern is never sufficient to declare a part non-OEM").
export const OEM_FORMATS = [
  // Mopar: eight numeric characters + a two-letter revision suffix (68461656AC), plus the
  // legitimate reman / service-package / accessory prefixed forms.
  { family: "MOPAR", re: /^(?:R|RL|RM|K|82|CB)?\d{8}(?:[A-Z]{2})?$/, label: "Mopar 8-digit (+2-letter revision)" },
  { family: "BMW", re: /^\d{11}$/, label: "BMW/MINI 11-digit" },
  { family: "TOYOTA", re: /^\d{5}-\d{5}(?:-\d{2})?$/, label: "Toyota/Lexus 5-5(-2)" },
  { family: "HONDA", re: /^\d{5}-[A-Z0-9]{3}-[A-Z0-9]{4}$/, label: "Honda/Acura 5-3-4" },
  { family: "NISSAN", re: /^[A-Z0-9]{5}-[A-Z0-9]{5}$/, label: "Nissan/Infiniti 5-5" },
  { family: "HYUNDAI", re: /^\d{5}[A-Z]?-[A-Z0-9]{5,6}$/, label: "Hyundai/Kia 5+5" },
  { family: "FORD", re: /^[A-Z0-9]{2}\d[Z]?-\d{4,5}-[A-Z]{1,3}$/, label: "Ford ##Z-####-A" },
  { family: "MERCEDES", re: /^A?\d{10}(?:\d{2})?$/, label: "Mercedes A + 10-digit" },
];

// Words in a description/brand field that SAY what the part is. Weak signals (playbook §6).
export const DESC_OEM_RE = /\b(?:oem|o\.e\.m\.|genuine|factory|mopar|motorcraft|acdelco|original\s*equipment)\b/i;
export const DESC_AFTERMARKET_RE = /\b(?:aftermarket|a\/m\b|non-?oem|will\s*fit|economy\s*line|white\s*box)\b/i;

// ══════════════════════════════════════════════════════════════════════════
//  APPROVED-MAPPING LEDGER — playbook §1 and §Review-and-learning.
//  Entries are versioned and IMMUTABLE: a correction is a new entry that supersedes
//  the old decisionId, a withdrawal is a revocation entry. Nothing is ever edited in
//  place, so any past file can be reproduced from its decision IDs.
// ══════════════════════════════════════════════════════════════════════════

export const MAPPING_SCOPES = ["dealer", "group", "dms", "global"];
// Narrowest first — playbook: "reuse the narrowest applicable approved mapping".
const SCOPE_ORDER = ["dealer", "group", "dms", "global"];
const SCOPE_EVIDENCE = { dealer: "MAPPING_DEALER", group: "MAPPING_GROUP", dms: "MAPPING_DMS", global: "MAPPING_GLOBAL" };

const mapKey = (scope, scopeKey, normalized) =>
  `${scope}|${scope === "global" ? "*" : String(scopeKey || "").trim().toUpperCase()}|${normalized}`;

/**
 * Fold the append-only ledger into the set of ACTIVE mappings.
 * Revocations and supersessions remove earlier decisions; nothing is deleted.
 * Returns { byKey: Map<key, entries[]>, conflicts: Set<key>, revoked: Set<decisionId> }.
 */
export function buildMappingIndex(entries) {
  const rows = (Array.isArray(entries) ? entries : []).filter(e => e && typeof e === "object");
  const revoked = new Set();
  const superseded = new Set();
  for (const e of rows) {
    if (e.revokes) revoked.add(String(e.revokes));
    if (e.revoked === true && e.decisionId) revoked.add(String(e.decisionId));
    if (e.supersedes) superseded.add(String(e.supersedes));
  }
  const byKey = new Map();
  const conflicts = new Set();
  for (const e of rows) {
    if (e.revokes && !e.verdict) continue;                        // pure revocation record
    if (!e.verdict || !PART_CLASSES.includes(e.verdict)) continue;
    if (!e.normalized && !e.partNumber) continue;
    const id = e.decisionId ? String(e.decisionId) : null;
    if (id && (revoked.has(id) || superseded.has(id))) continue;
    const scope = MAPPING_SCOPES.includes(e.scope) ? e.scope : "global";
    const normalized = String(e.normalized || e.partNumber).trim().toUpperCase();
    const k = mapKey(scope, e.scopeKey, normalized);
    const list = byKey.get(k) || [];
    list.push({ ...e, scope, normalized, decisionId: id });
    byKey.set(k, list);
    if (list.length > 1 && list.some(x => x.verdict !== list[0].verdict)) conflicts.add(k);
  }
  return { byKey, conflicts, revoked };
}

/**
 * Resolve the narrowest applicable approved mapping.
 * A same-scope disagreement is a REFUSAL, not a vote (playbook fail-loud #1).
 */
export function resolveMapping(index, part) {
  if (!index || !index.byKey) return null;
  const candidates = [
    ["dealer", part.dealer], ["group", part.dealerGroup], ["dms", part.dms], ["global", null],
  ];
  const numbers = [part.normalized, part.strippedKey].filter(Boolean);
  for (const [scope, scopeKey] of candidates) {
    if (scope !== "global" && !scopeKey) continue;
    for (const number of numbers) {
      const k = mapKey(scope, scopeKey, number);
      const list = index.byKey.get(k);
      if (!list || !list.length) continue;
      if (index.conflicts.has(k)) {
        return { conflict: true, scope, tier: scope, entries: list, matchedOn: number };
      }
      // Same verdict recorded more than once (re-approval): the newest wins, and they agree.
      const entry = list[list.length - 1];
      return { conflict: false, scope, tier: scope, entry, matchedOn: number };
    }
  }
  return null;
}

// ══════════════════════════════════════════════════════════════════════════
//  PER-PART CLASSIFICATION — the playbook's decision hierarchy, strongest first.
// ══════════════════════════════════════════════════════════════════════════

const num = (v) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };
const hasMoney = (v) => v !== null && v !== undefined && v !== "" && Number.isFinite(Number(v));

function matchAftermarketBrand(text) {
  for (const b of AFTERMARKET_BRANDS) if (b.re.test(text)) return b;
  return null;
}
function matchSupplierBrand(text) {
  for (const b of OE_SUPPLIER_BRANDS) if (b.re.test(text)) return b;
  return null;
}
function matchAftermarketFormat(normalized) {
  for (const f of AFTERMARKET_FORMATS) if (f.re.test(normalized)) return f;
  return null;
}
// Place a number with ONE tier-1 catalog, or refuse to. Two matching patterns means the
// number is not placeable, and an unplaceable number never confirms anything.
export function attributeByNumber(normalized) {
  if (!normalized) return null;
  const hits = AFTERMARKET_BRANDS.filter(b => b.confirmsByNumber && b.format && b.format.test(normalized));
  return hits.length === 1 ? hits[0] : null;
}
function matchOemFormats(normalized, strippedKey) {
  const hits = [];
  for (const f of OEM_FORMATS) {
    if (f.re.test(normalized) || (strippedKey && f.re.test(strippedKey))) hits.push(f);
  }
  return hits;
}

/**
 * Classify ONE part. `ctx` carries the injected evidence:
 *   { mappingIndex, aiVerdicts, dealer, dealerGroup, dms, vehicleMake, config }
 * Returns the playbook's output contract plus the audit detail the review page needs.
 */
export function classifyPart(rawPart, ctx = {}) {
  const config = { ...PARTS_CONFIG, ...(ctx.config || {}) };
  const part = rawPart || {};
  const vehicleMake = canonicalMake(part.vehicleMake ?? ctx.vehicleMake);
  const vehicleFamily = makeFamily(vehicleMake);
  const { raw, normalized, strippedKey } = normalizePartNumber(part.partNumber, { make: vehicleMake });
  const description = String(part.partDesc || part.description || "").trim();
  const brandField = String(part.brand || part.manufacturer || "").trim();
  const haystack = `${description} ${brandField}`;

  const evidenceCodes = [];
  const fails = [];
  const out = {
    classification: "UNKNOWN",
    confidence: CONF.none,
    evidenceCodes,
    matchedMake: null,
    matchedBrand: null,
    ruleVersion: OEM_PART_RULE_VERSION,
    mappingDecisionId: null,
    requiresReview: false,
  };
  // Audit detail — never part of the decision, always shown to the reviewer.
  const detail = {
    raw, normalized, strippedKey, description, brand: brandField || null,
    vehicleMake, sale: hasMoney(part.sale) ? num(part.sale) : null,
    cost: hasMoney(part.cost) ? num(part.cost) : null,
    quantity: hasMoney(part.quantity) ? num(part.quantity) : null,
    active: part.active !== false, returned: part.returned === true,
    sourceVendor: part.sourceVendor || null, stockGroup: part.stockGroup || null,
    oemIndicator: part.oemIndicator === undefined ? null : part.oemIndicator,
    ro: part.ro ?? null, line: part.line ?? null,
    configVersion: config.version,
  };

  const finish = (classification, confidence, extra = {}) => {
    out.classification = classification;
    out.confidence = confidence;
    Object.assign(out, extra);
    out.requiresReview = out.requiresReview
      || classification === "UNKNOWN" || classification === "NON_OEM_PROBABLE" || fails.length > 0;
    out.claimable = classification === "OEM_CONFIRMED" || classification === "OEM_PROBABLE" || classification === "FLUID";
    out.detail = detail;
    out.fails = fails;
    return out;
  };

  // ── 1. Approved exact mappings — decisive until revoked ────────────────────
  const mapping = resolveMapping(ctx.mappingIndex, {
    normalized, strippedKey, dealer: part.dealer ?? ctx.dealer,
    dealerGroup: part.dealerGroup ?? ctx.dealerGroup, dms: part.dms ?? ctx.dms,
  });
  if (mapping && mapping.conflict) {
    evidenceCodes.push("MAPPING_CONFLICT");
    fails.push({
      code: "MAPPING_CONFLICT", scope: mapping.scope, partNumber: raw,
      decisionIds: mapping.entries.map(e => e.decisionId).filter(Boolean),
      message: `Approved ${mapping.scope} mappings disagree for ${raw || "(no number)"} — refusing to guess.`,
    });
    out.requiresReview = true;
    return finish("UNKNOWN", CONF.none);
  }
  if (mapping && mapping.entry) {
    evidenceCodes.push(SCOPE_EVIDENCE[mapping.scope]);
    const e = mapping.entry;
    return finish(e.verdict, CONF.mapping, {
      matchedBrand: e.brand || null,
      matchedMake: canonicalMake(e.make) || null,
      mappingDecisionId: e.decisionId || null,
    });
  }

  // ── 2. Fluid / chemical vocabulary ─────────────────────────────────────────
  // Category first, brand/OEM status retained as separate evidence (playbook §5).
  // Chemical is tested before fluid: "coolant system cleaner" names a fluid but is a chemical.
  const chemical = CHEMICAL_SUPPLY_RE.test(haystack);
  const fluid = !chemical && FLUID_RE.test(haystack);
  if (chemical || fluid) {
    evidenceCodes.push(chemical ? "VOCAB_CHEMICAL" : "VOCAB_FLUID");
    const oemWord = DESC_OEM_RE.test(haystack);
    if (oemWord) evidenceCodes.push("DESC_OEM_WORD");
    const am = matchAftermarketBrand(haystack);
    if (am) evidenceCodes.push("BRAND_AFTERMARKET");
    return finish(chemical ? "CHEMICAL_SUPPLY" : "FLUID", CONF.vocab, {
      matchedBrand: am ? am.brand : (oemWord ? (brandField || null) : null),
    });
  }

  // ── 3. Authoritative source indicators ─────────────────────────────────────
  const flag = part.oemIndicator;
  const flagSaysOem = flag === true || /^(y|yes|oem|true|1)$/i.test(String(flag ?? ""));
  const flagSaysAftermarket = flag === false || /^(n|no|aftermarket|a\/m|false|0)$/i.test(String(flag ?? ""));
  const brandAftermarket = matchAftermarketBrand(haystack);
  const brandSupplier = !brandAftermarket && matchSupplierBrand(haystack);
  const brandFormatHit = brandAftermarket && brandAftermarket.format && brandAftermarket.format.test(normalized);

  // Fail-loud: an authoritative OEM flag against a brand+format aftermarket match.
  if (flagSaysOem && brandFormatHit && config.trustSourceFlags) {
    evidenceCodes.push("SOURCE_FLAG_CONFLICT", "BRAND_AFTERMARKET", "FORMAT_AFTERMARKET");
    fails.push({
      code: "FLAG_VS_BRAND_CONFLICT", partNumber: raw, brand: brandAftermarket.brand,
      message: `DMS OEM flag says OEM but ${brandAftermarket.brand} brand + number format say aftermarket.`,
    });
    out.requiresReview = true;
    return finish("UNKNOWN", CONF.none, { matchedBrand: brandAftermarket.brand });
  }
  if (flagSaysOem || flagSaysAftermarket) {
    const oem = flagSaysOem;
    evidenceCodes.push(oem ? "SOURCE_OEM_FLAG" : "SOURCE_AFTERMARKET_FLAG");
    if (config.trustSourceFlags) {
      return finish(oem ? "OEM_CONFIRMED" : "NON_OEM_CONFIRMED", CONF.trustedFlag, { matchedMake: oem ? vehicleMake : null });
    }
    // Untrusted flag: real evidence, not proof. Keep looking for a second leg, but
    // remember the lean so an unmatched number still lands PROBABLE rather than UNKNOWN.
    evidenceCodes.push("SOURCE_FLAG_UNTRUSTED");
    if (!brandAftermarket && !brandSupplier) {
      const oemHits = matchOemFormats(normalized, strippedKey);
      const sameFamily = vehicleFamily && oemHits.some(h => h.family === vehicleFamily);
      if (oem) {
        if (sameFamily) evidenceCodes.push("FORMAT_OEM_MAKE");
        return finish("OEM_PROBABLE", sameFamily ? CONF.makeFormat : CONF.brandOnly, { matchedMake: vehicleMake });
      }
      return finish("NON_OEM_PROBABLE", CONF.brandOnly);
    }
  }

  // ── 4. Known aftermarket brand / format ────────────────────────────────────
  if (brandAftermarket) {
    evidenceCodes.push("BRAND_AFTERMARKET");
    if (brandFormatHit) {
      evidenceCodes.push("FORMAT_AFTERMARKET");
      return finish("NON_OEM_CONFIRMED", CONF.brandAndFormat, { matchedBrand: brandAftermarket.brand });
    }
    // Brand named but its numbering doesn't corroborate → probable, a human looks.
    return finish("NON_OEM_PROBABLE", CONF.brandOnly, { matchedBrand: brandAftermarket.brand });
  }
  if (brandSupplier) {
    // An OE supplier's name is never proof of aftermarket — the same company fills the OEM box.
    evidenceCodes.push("BRAND_SUPPLIER");
    return finish("NON_OEM_PROBABLE", CONF.formatOnly, { matchedBrand: brandSupplier.brand });
  }
  if (DESC_AFTERMARKET_RE.test(haystack)) {
    evidenceCodes.push("DESC_AFTERMARKET_WORD");
    return finish("NON_OEM_PROBABLE", CONF.brandOnly);
  }
  // The number alone, when it positively matches ONE tier-1 catalog's documented
  // numbering and the description agrees (Blake 2026-08-05).
  const attributed = attributeByNumber(normalized);
  if (attributed) {
    evidenceCodes.push("FORMAT_AFTERMARKET_BRANDED");
    if (descriptionSupportsAftermarket(description, haystack)) {
      return finish("NON_OEM_CONFIRMED", CONF.brandAndFormat, { matchedBrand: attributed.brand });
    }
    // Placeable number, but the description is blank or claims OEM — evidence disagrees.
    return finish("NON_OEM_PROBABLE", CONF.formatOnly, { matchedBrand: attributed.brand });
  }
  const amFormat = normalized && matchAftermarketFormat(normalized);
  if (amFormat) {
    // Shape only. The playbook is explicit that shape alone never confirms.
    evidenceCodes.push("FORMAT_AFTERMARKET");
    return finish("NON_OEM_PROBABLE", CONF.formatOnly, { matchedBrand: null });
  }

  // ── 5. OEM catalog / dealer-family pattern ─────────────────────────────────
  const oemHits = normalized ? matchOemFormats(normalized, strippedKey) : [];
  const oemWordHit = DESC_OEM_RE.test(haystack);
  if (oemHits.length) {
    const sameFamily = vehicleFamily && oemHits.some(h => h.family === vehicleFamily);
    if (sameFamily) {
      evidenceCodes.push("FORMAT_OEM_MAKE");
      if (oemWordHit) evidenceCodes.push("DESC_OEM_WORD");
      // A pattern match without a catalog match is PROBABLE — never CONFIRMED (§4).
      return finish("OEM_PROBABLE", CONF.makeFormat, { matchedMake: vehicleMake, matchedBrand: oemWordHit ? (brandField || null) : null });
    }
    if (!vehicleFamily) {
      // No make column: the format still says "an OEM catalog", just not whose.
      evidenceCodes.push("FORMAT_OEM_MAKE", "MAKE_UNKNOWN");
      return finish("OEM_PROBABLE", CONF.formatOnly, { matchedMake: oemHits[0].family });
    }
    // A different make's OEM format on this vehicle. NEVER aftermarket on that basis
    // alone (§4) — it is unresolved, and a human decides.
    evidenceCodes.push("FORMAT_OEM_OTHER_MAKE");
    out.requiresReview = true;
    return finish("UNKNOWN", CONF.none, { matchedMake: oemHits[0].family });
  }
  if (oemWordHit) {
    evidenceCodes.push("DESC_OEM_WORD");
    return finish("OEM_PROBABLE", CONF.brandOnly, { matchedMake: vehicleMake, matchedBrand: brandField || null });
  }

  // ── 6. Injected AI brand/OEM verdict — heuristic-only, caps at PROBABLE ────
  const ai = ctx.aiVerdicts && (ctx.aiVerdicts[normalized] || (description && ctx.aiVerdicts[description.toUpperCase()]));
  if (ai && ai.verdict && ai.verdict !== "unknown") {
    const aiConf = Number.isFinite(Number(ai.confidence)) ? Math.min(0.85, Number(ai.confidence) / 100) : CONF.ai;
    if (ai.verdict === "oem") {
      evidenceCodes.push("AI_OEM");
      return finish("OEM_PROBABLE", aiConf, { matchedMake: canonicalMake(ai.make) || vehicleMake, matchedBrand: ai.brand || null });
    }
    if (ai.verdict === "aftermarket") {
      evidenceCodes.push("AI_AFTERMARKET");
      return finish("NON_OEM_PROBABLE", aiConf, { matchedBrand: ai.brand || null });
    }
    if (ai.verdict === "fluid") { evidenceCodes.push("AI_FLUID"); return finish("FLUID", aiConf, { matchedBrand: ai.brand || null }); }
    if (ai.verdict === "chemical") { evidenceCodes.push("AI_CHEMICAL"); return finish("CHEMICAL_SUPPLY", aiConf, { matchedBrand: ai.brand || null }); }
  }
  if (ai) evidenceCodes.push("AI_UNCERTAIN");

  // ── 7. Unresolved. UNKNOWN never becomes OEM to keep a line in the sample. ──
  if (!normalized) evidenceCodes.push("NO_PART_NUMBER");
  else evidenceCodes.push("NO_EVIDENCE");
  out.requiresReview = true;
  return finish("UNKNOWN", CONF.none, { matchedMake: null });
}

// ══════════════════════════════════════════════════════════════════════════
//  LINE-LEVEL ROLLUP + ROUTING — playbook §Line-level decision rules, merged with
//  Blake's 2026-08-05 rulings (anatomy-informed main part; non-OEM main part is the
//  disqualifier, incidental chemicals never are).
// ══════════════════════════════════════════════════════════════════════════

const CATEGORY_OF = {
  OEM_CONFIRMED: "oem", OEM_PROBABLE: "oem",
  NON_OEM_CONFIRMED: "nonOem", NON_OEM_PROBABLE: "nonOem",
  FLUID: "fluid", CHEMICAL_SUPPLY: "chemical", UNKNOWN: "unknown",
};
const HARD_PART = new Set(["oem", "nonOem", "unknown"]);

// Tokens that never identify a component on their own when matching anatomy → part.
const LABEL_STOP = new Set(["assembly", "assy", "kit", "set", "front", "rear", "left", "right",
  "upper", "lower", "inner", "outer", "and", "the", "of", "for", "with", "new", "oem", "genuine", "part"]);

const labelTokens = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9 ]+/g, " ")
  .split(/\s+/).filter(t => t.length > 2 && !LABEL_STOP.has(t));

/**
 * Blake's ruling 3: the MAIN part is the one matching the AI-extracted repair component
 * (🧩 anatomy). Dominant-by-sale is the FALLBACK, used only when no part matches the
 * component — so a caliper job's main part is the caliper even if a rotor sold for more.
 */
export function pickMainPart(results, component) {
  const hard = results.filter(r => HARD_PART.has(CATEGORY_OF[r.classification]));
  const pool = hard.length ? hard : results;
  const tokens = labelTokens(component);
  if (tokens.length) {
    const scored = pool.map(r => {
      const text = labelTokens(`${r.detail.description} ${r.detail.brand || ""}`);
      const hits = tokens.filter(t => text.includes(t)).length;
      return { r, hits };
    }).filter(x => x.hits > 0);
    if (scored.length) {
      scored.sort((a, b) => b.hits - a.hits || num(b.r.detail.sale) - num(a.r.detail.sale));
      return { part: scored[0].r, basis: "anatomy" };
    }
  }
  const bySale = [...pool].sort((a, b) => num(b.detail.sale) - num(a.detail.sale));
  if (!bySale.length) return { part: null, basis: "none" };
  if (!bySale.some(r => num(r.detail.sale) > 0)) return { part: bySale[0], basis: "dominant-by-sale" };
  return { part: bySale[0], basis: "dominant-by-sale" };
}

/**
 * Classify a whole operation's parts basket.
 *   line: { ro, line, parts[], partsSale, partsCost, component }
 *   ctx:  { mappingIndex, aiVerdicts, dealer, dealerGroup, dms, vehicleMake, config }
 * Returns { parts[], rollup, routing, fails } — FACTS plus a routing code. It never
 * decides the line: WP-P2 combines this with the repair verdict.
 */
export function classifyLineParts(line = {}, ctx = {}) {
  const config = { ...PARTS_CONFIG, ...(ctx.config || {}) };
  const fails = [];

  if (!line.ro && line.ro !== 0) {
    fails.push({ code: "NO_STABLE_OPERATION", message: "Parts basket has no RO/operation to attach to." });
  }
  if (!OEM_PART_RULE_VERSION || !config.version) {
    fails.push({ code: "RULE_VERSION_MISSING", message: "Rule or config version unavailable — result is not reproducible." });
  }

  const all = Array.isArray(line.parts) ? line.parts : [];
  // "Emit a result for every active part." Deleted/returned parts are kept out of the
  // math but never silently dropped — they ride along as inactive audit evidence.
  const activeParts = all.filter(p => p && p.active !== false && p.returned !== true);
  const inactiveParts = all.filter(p => p && (p.active === false || p.returned === true));

  const partCtx = { ...ctx, config, vehicleMake: ctx.vehicleMake ?? line.vehicleMake };
  const results = activeParts.map(p => classifyPart(p, partCtx));
  const inactiveResults = inactiveParts.map(p => ({ ...classifyPart(p, partCtx), inactive: true }));
  for (const r of results) for (const f of r.fails || []) fails.push({ ...f, ro: line.ro ?? null, line: line.line ?? null });

  // Normalization collision: two DIFFERENT raw numbers that normalize to one key.
  const byNormalized = new Map();
  for (const r of results) {
    const k = r.detail.normalized;
    if (!k) continue;
    const seen = byNormalized.get(k);
    if (seen && seen !== r.detail.raw) {
      fails.push({ code: "NORMALIZED_COLLISION", key: k, raws: [seen, r.detail.raw],
        message: `Distinct part numbers "${seen}" and "${r.detail.raw}" normalize to the same key.` });
      r.evidenceCodes.push("NORMALIZED_COLLISION");
      r.requiresReview = true;
    } else if (!seen) byNormalized.set(k, r.detail.raw);
  }

  // ── Money rollups ─────────────────────────────────────────────────────────
  const moneyAvailable = results.some(r => r.detail.sale !== null || r.detail.cost !== null);
  const zero = () => ({ oem: 0, nonOem: 0, fluid: 0, chemical: 0, unknown: 0 });
  const saleBy = zero(), costBy = zero();
  let totalSale = 0, totalCost = 0;
  for (const r of results) {
    const cat = CATEGORY_OF[r.classification];
    const s = num(r.detail.sale), c = num(r.detail.cost);
    saleBy[cat] += s; costBy[cat] += c;
    totalSale += s; totalCost += c;
  }
  const share = (v) => (totalSale > 0 ? v / totalSale : null);
  const costShare = (v) => (totalCost > 0 ? v / totalCost : null);

  // Claimable dollars — Blake's ruling 2: OEM hard parts + installed fluids. Aftermarket
  // hard parts, chemicals/supplies and UNKNOWN are stripped from the money even when the
  // line itself is included.
  const claimableSale = saleBy.oem + saleBy.fluid;
  const claimableCost = costBy.oem + costBy.fluid;

  // Reconciliation (playbook fail-loud): the basket must add up to the operation's total.
  if (moneyAvailable && hasMoney(line.partsSale)) {
    const stated = num(line.partsSale);
    const tolerance = Math.max(config.reconcileToleranceAbs, Math.abs(stated) * config.reconcileToleranceShare);
    if (Math.abs(stated - totalSale) > tolerance) {
      fails.push({ code: "PARTS_TOTAL_MISMATCH", stated, basket: Number(totalSale.toFixed(2)), tolerance,
        message: `Parts basket totals $${totalSale.toFixed(2)} but the operation's parts sale is $${stated.toFixed(2)}.` });
    }
  }

  // A materially-priced part with no usable number and no manufacturer is unidentifiable.
  for (const r of results) {
    if (!r.detail.normalized && !r.detail.brand) {
      const sh = share(num(r.detail.sale));
      if (sh !== null && sh >= config.materialUnknownShare) {
        fails.push({ code: "MATERIAL_PART_UNIDENTIFIABLE", description: r.detail.description, sale: num(r.detail.sale),
          message: `"${r.detail.description || "(no description)"}" is ${(sh * 100).toFixed(1)}% of line parts sale with no part number and no manufacturer.` });
      }
    }
  }

  const nonOemSaleShare = share(saleBy.nonOem);
  const chemicalSaleShare = share(saleBy.chemical + saleBy.fluid);   // routing share: BOTH categories
  const unknownSaleShare = share(saleBy.unknown);
  const dominantCategoryBySale = totalSale > 0
    ? Object.keys(saleBy).reduce((a, b) => (saleBy[b] > saleBy[a] ? b : a))
    : null;

  const main = pickMainPart(results, line.component ?? ctx.component);
  const containsNonOem = results.some(r => CATEGORY_OF[r.classification] === "nonOem");
  const containsUnknown = results.some(r => CATEGORY_OF[r.classification] === "unknown");
  const hardParts = results.filter(r => HARD_PART.has(CATEGORY_OF[r.classification]));

  const rollup = {
    ro: line.ro ?? null, line: line.line ?? null,
    moneyAvailable,
    saleByCategory: saleBy, costByCategory: costBy,
    totalSale: Number(totalSale.toFixed(2)), totalCost: Number(totalCost.toFixed(2)),
    saleShareByCategory: {
      oem: share(saleBy.oem), nonOem: nonOemSaleShare, fluid: share(saleBy.fluid),
      chemical: share(saleBy.chemical), unknown: unknownSaleShare,
    },
    costShareByCategory: {
      oem: costShare(costBy.oem), nonOem: costShare(costBy.nonOem), fluid: costShare(costBy.fluid),
      chemical: costShare(costBy.chemical), unknown: costShare(costBy.unknown),
    },
    nonOemSaleShare, chemicalSaleShare, unknownSaleShare,
    dominantCategoryBySale,
    containsNonOem,
    nonOemDominant: nonOemSaleShare !== null && nonOemSaleShare >= config.nonOemDominantShare,
    containsUnknown,
    claimableSale: Number(claimableSale.toFixed(2)), claimableCost: Number(claimableCost.toFixed(2)),
    claimableMarkupPct: claimableCost > 0 ? ((claimableSale - claimableCost) / claimableCost) * 100 : null,
    mainPart: main.part ? {
      description: main.part.detail.description, partNumber: main.part.detail.raw,
      classification: main.part.classification, sale: main.part.detail.sale, basis: main.basis,
    } : null,
    partCount: results.length, inactiveCount: inactiveResults.length,
    configVersion: config.version, ruleVersion: OEM_PART_RULE_VERSION,
  };

  const routing = routeLine({ results, rollup, main, hardParts, fails, config });
  return { parts: results, inactiveParts: inactiveResults, rollup, routing, fails };
}

// The routing table. Order IS the law: the first matching row wins.
function routeLine({ results, rollup, main, hardParts, fails, config }) {
  const mainClass = main.part ? main.part.classification : null;
  const mk = (route, verdict, reason, extra = {}) => ({
    route, verdict, reason, mainPart: rollup.mainPart, configVersion: config.version, ...extra,
  });

  if (!results.length) {
    return mk("NO_PARTS_DATA", "unknown", "No parts recorded on this operation — nothing to judge.");
  }
  if (fails.length) {
    return mk("PARTS_FAIL_LOUD", "review",
      `Parts evidence could not be trusted: ${fails.map(f => f.code).join(", ")}.`, { failCodes: fails.map(f => f.code) });
  }
  if (mainClass === "NON_OEM_CONFIRMED") {
    const p = main.part.detail;
    return mk("NON_OEM_MAIN_PART", "ineligible",
      `PARTS: aftermarket ${p.description || p.raw} is the line's main part${main.part.matchedBrand ? ` (${main.part.matchedBrand})` : ""} — not claimable in a parts uplift.`);
  }
  if (mainClass === "NON_OEM_PROBABLE") {
    const p = main.part.detail;
    return mk("MIXED_PARTS_REVIEW", "review",
      `PARTS: the line's main part ${p.description || p.raw} looks aftermarket but is not confirmed — verify the part.`);
  }
  if (rollup.nonOemDominant) {
    return mk("NON_OEM_DOMINANT", "review",
      `PARTS: ${(rollup.nonOemSaleShare * 100).toFixed(0)}% of this line's parts sale is non-OEM — verify what was installed.`);
  }
  if (rollup.unknownSaleShare !== null && rollup.unknownSaleShare >= config.materialUnknownShare) {
    return mk("UNKNOWN_PART_REVIEW", "review",
      `PARTS: ${(rollup.unknownSaleShare * 100).toFixed(0)}% of this line's parts sale is a part we could not identify — verify it.`);
  }
  if (rollup.containsUnknown && !rollup.moneyAvailable) {
    return mk("UNKNOWN_PART_REVIEW", "review",
      "PARTS: an unidentified part is on this line and there is no per-part pricing to weigh it — verify it.");
  }
  if (!hardParts.length) {
    // Chemicals/fluids only: a standalone treatment or fluid service, not a parts repair.
    return mk("CHEMICAL_SERVICE_REVIEW", "review",
      "PARTS: this line's parts are chemicals/fluids only, with no repair part — verify what was sold.");
  }
  if (rollup.containsNonOem) {
    return mk("MIXED_PARTS_REVIEW", "review",
      "PARTS: a non-OEM part is on the line alongside the main repair part — verify which parts belong to the claim.");
  }
  if (rollup.chemicalSaleShare !== null && rollup.chemicalSaleShare > 0 && rollup.chemicalSaleShare <= config.incidentalChemicalShare) {
    return mk("INCIDENTAL_CHEMICAL", "eligible",
      `PARTS: chemicals/fluids are ${(rollup.chemicalSaleShare * 100).toFixed(1)}% of this line — incidental to the repair, the repair part stands.`);
  }
  if (rollup.chemicalSaleShare !== null && rollup.chemicalSaleShare > config.incidentalChemicalShare) {
    return mk("CHEMICAL_SERVICE_REVIEW", "review",
      `PARTS: chemicals/fluids are ${(rollup.chemicalSaleShare * 100).toFixed(0)}% of this line's parts sale — verify whether this is a repair or a chemical service.`);
  }
  return mk("OEM_CLEAN", "eligible", "PARTS: every priced part on this line is OEM.");
}

// ══════════════════════════════════════════════════════════════════════════
//  AI PASS PLUMBING — the prompt lives in prompts.js (PARTS_OEM_SYS); the fetch lives
//  in the caller (WP-P2). These two pure helpers are the seam.
// ══════════════════════════════════════════════════════════════════════════

/** Build the AI payload for a set of parts: one entry per distinct normalized number. */
export function buildOemAiPayload(parts, ctx = {}) {
  const seen = new Set();
  const payload = [];
  for (const p of parts || []) {
    const make = canonicalMake(p?.vehicleMake ?? ctx.vehicleMake);
    const { normalized } = normalizePartNumber(p?.partNumber, { make });
    const description = String(p?.partDesc || p?.description || "").trim();
    const id = normalized || description.toUpperCase();
    if (!id || seen.has(id)) continue;
    seen.add(id);
    payload.push({ id, partNumber: p?.partNumber ?? "", description, brand: p?.brand || p?.manufacturer || "", vehicleMake: make || "" });
  }
  return payload;
}

/** Fold the AI's array response into the { [id]: verdict } map classifyPart consumes. */
export function mergeAiVerdicts(rows) {
  const out = {};
  for (const r of Array.isArray(rows) ? rows : []) {
    if (!r || !r.id) continue;
    const verdict = String(r.verdict || "").toLowerCase();
    if (!["oem", "aftermarket", "fluid", "chemical", "unknown"].includes(verdict)) continue;
    out[String(r.id).toUpperCase()] = {
      verdict, brand: r.brand || null, make: r.make || null,
      confidence: Number.isFinite(Number(r.confidence)) ? Number(r.confidence) : null,
      why: r.why || "",
    };
  }
  return out;
}
